// Grouped, searchable demo-persona picker — same DEMO_USERS source + email
// derivation as v1's DemoUserSwitcher (src/components/demo). v1's antd Menu
// renders the section markers as flat disabled rows; here each marker starts
// a real CommandGroup instead (equivalent grouping, native to the primitive).
import { useSwitchIdentity } from '@/lib/hooks/useSwitchIdentity';
import { DEMO_USERS, resolveDemoUserEmail, type DemoUser } from '@/types/demo';
import { LoginLinear } from '@assessprep/icons/solar';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Spinner } from '@/components/ui/spinner';

export interface DemoAccountsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DemoGroup {
  title?: string;
  users: DemoUser[];
}

const groupDemoUsers = (users: DemoUser[]): DemoGroup[] => {
  const groups: DemoGroup[] = [];
  let current: DemoGroup = { users: [] };
  for (const entry of users) {
    if (entry.showTitle) {
      if (current.users.length) groups.push(current);
      current = { title: entry.name, users: [] };
    } else {
      current.users.push(entry);
    }
  }
  if (current.users.length) groups.push(current);
  return groups;
};

export const DemoAccountsDialog = ({ open, onOpenChange }: DemoAccountsDialogProps) => {
  const { t } = useTranslation();
  const { switchIdentity, pendingId } = useSwitchIdentity();
  const groups = useMemo(() => groupDemoUsers(DEMO_USERS), []);

  return (
    <CommandDialog
      open={open}
      onOpenChange={(next) => !pendingId && onOpenChange(next)}
      title={t('V4Shell.demo_switch_user')}
      description={t('V4Shell.demo_switch_user_description')}
    >
      <CommandInput
        placeholder={t('V4Shell.demo_search_placeholder')}
        data-testid="v4-demo-bar-search"
      />
      <CommandList data-testid="v4-demo-bar-list">
        <CommandEmpty>{t('CommonText.no_data')}</CommandEmpty>
        {groups.map((group, groupIndex) => (
          <CommandGroup
            key={group.title ?? `default-${groupIndex}`}
            heading={group.title ?? t('V4Shell.demo_default_group')}
          >
            {group.users.map((user) => {
              const email = resolveDemoUserEmail(user);
              const key = user.key ?? user.name ?? String(email);
              const isPending = pendingId === (user.key ?? email);
              const handleSelect = () => {
                if (!email || pendingId) return;
                // Close immediately (v1 parity — the Dropdown menu closed on
                // click regardless of outcome); the switchIdentity toast
                // reports success/failure after the dialog is gone.
                onOpenChange(false);
                switchIdentity({ id: user.key, email });
              };
              return (
                <CommandItem
                  key={key}
                  value={`${user.name ?? ''} ${user.role ?? ''} ${email ?? ''}`}
                  disabled={!email || !!pendingId}
                  onSelect={handleSelect}
                  data-testid="v4-demo-bar-user-option"
                  data-demo-user={user.key}
                >
                  {isPending ? <Spinner aria-hidden="true" /> : <LoginLinear aria-hidden="true" />}
                  <span className="flex-1 truncate">{user.name}</span>
                  {user.role && <span className="text-xs text-muted-foreground">{user.role}</span>}
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};
